<template>
  <v-container>
    <v-progress-circular
      v-if="loading"
      indeterminate
      color="primary"
      size="64"
      class="d-flex justify-center"
    ></v-progress-circular>
    <div v-else class="custom-class" style="display: flex; gap: 20px">
      <v-card
        class="mx-auto bg-white lighten-1 pad-to-add-coup"
        style="max-width: 800px"
      >
        <v-card-title align="center" class="pb-2">Add Coupon</v-card-title>
        <v-card-title class="pb-2 font-weight-bold">General</v-card-title>
        <v-card-text>
          <v-form ref="couponForm" v-model="valid">
            <!-- Title Field -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="title">Title<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="coupon.title"
                  :counter="30"
                  :maxlength="30"
                  :rules="[v => (v?.length ?? 0) <= 30 || 'Max 30 characters']"
                  label="Title"
                  density="compact"
                  required
                  data-testid="addcoupon-title"
                />
              </v-col>
            </v-row>

            <!-- Description Field -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="description">Description<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-textarea
                  v-model="coupon.description"
                  :counter="70"
                  :maxlength="70"
                  :rules="[v => (v?.length ?? 0) <= 70 || 'Max 70 characters']"
                  rows="1"
                  label="Description"
                  density="compact"
                  required
                  data-testid="addcoupon-description"
                />
              </v-col>
            </v-row>

            <!-- Disclaimer Field -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="disclaimer">Disclaimer<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-textarea
                  v-model="coupon.disclaimer"
                  :counter="100"
                  :maxlength="100"
                  :rules="[
                    v => (v?.length ?? 0) <= 100 || 'Max 100 characters',
                  ]"
                  rows="1"
                  label="Disclaimer"
                  density="compact"
                  required
                  data-testid="addcoupon-disclaimer"
                />
              </v-col>
            </v-row>

            <!-- Link Label Field -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="linkLabel">Link Label:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  label="Link Label"
                  v-model="coupon.linkLabel"
                  :counter="50"
                  :maxlength="50"
                  :rules="[v => (v?.length ?? 0) <= 50 || 'Max 50 characters']"
                  density="compact"
                  placeholder="e.g., Shop Now, Learn More"
                  data-testid="addcoupon-linklabel"
                />
              </v-col>
            </v-row>

            <!-- Link URL Field -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="link">Link URL:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  label="Link URL"
                  v-model="coupon.link"
                  :rules="[
                    v =>
                      !v ||
                      /^(https?:\/\/.+|\/.*|#.*|mailto:|tel:)/.test(v) ||
                      'Please enter a valid URL (absolute, relative, or protocol)',
                  ]"
                  density="compact"
                  placeholder="https://example.com or /relative/path"
                  data-testid="addcoupon-link"
                />
              </v-col>
            </v-row>

            <!-- Start Time Field -->
            <v-row>
              <v-col cols="12" sm="4">
                <label for="start-time">Start Time<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8" class="mb-4">
                <div class="">
                  <input
                    type="datetime-local"
                    name="start-date"
                    @change="StatusChange"
                    class="custom-datepicker"
                    v-model="coupon.startTime"
                    outlined
                    dense
                    id="start-date"
                    @click="handleClickDatePicker"
                    data-testid="addcoupon-start"
                  />
                </div>
                <div v-if="errors.startTime" class="error-message">
                  {{ errors.startTime }}
                </div>
              </v-col>
            </v-row>

            <!-- End Time Field -->
            <v-row>
              <v-col cols="12" sm="4">
                <label for="start-time">End Time<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8" class="mb-4">
                <!-- <div class="datepicker-wrapper">
                  <flat-pickr v-model="coupon.endTime" :error-messages="errors.endTime" :config="config"
                    class="custom-datepicker" placeholder="mm/dd/yyyy --:-- --" />
                  <v-icon class="calendar-icon">mdi-calendar</v-icon>
                </div> -->
                <div class="">
                  <input
                    type="datetime-local"
                    name="end-date"
                    @change="EndStatusChange"
                    class="custom-datepicker"
                    v-model="coupon.endTime"
                    outlined
                    dense
                    id="end-date"
                    @click="handleClickDatePicker"
                    data-testid="addcoupon-end"
                  />
                  <!-- <v-text-field v-model="coupon.endTime" type="datetime-local" dense outlined /> -->
                </div>
                <div v-if="errors.endTime" class="error-message">
                  {{ errors.endTime }}
                </div>
              </v-col>
            </v-row>

            <!-- Usage Limit -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="Usage Limit">Usage Limit<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  label="Usage Limit"
                  v-model="coupon.usageLimit"
                  :error-messages="errors.usageLimit"
                  :min="0"
                  type="number"
                  required
                  outlined
                  data-testid="addcoupon-usage"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Minimum Purchase Amount -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="Minimum Purchase Amount"
                  >Minimum Purchase Amount<span>*</span>:
                </label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-text-field
                  label="Minimum Purchase Amount"
                  v-model="coupon.minPurchaseAmount"
                  :error-messages="errors.minPurchaseAmount"
                  :min="0"
                  type="number"
                  required
                  outlined
                  data-testid="addcoupon-min"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Discount Type -->
            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="Discount Type">Discount Type<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  id="discount-type"
                  v-model="coupon.discountType"
                  :items="discountOptions"
                  label="Discount Type"
                  density="compact"
                  required
                  :error-messages="errors.discountType"
                  data-testid="addcoupon-discount"
                ></v-select>
              </v-col>
            </v-row>

            <v-row class="mb-4">
              <v-col cols="12" sm="4">
                <label for="design">Status<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-select
                  id="design"
                  v-model="coupon.status"
                  :items="filteredStatusOptions"
                  item-value="value"
                  item-title="title"
                  density="compact"
                  ref="statusSelect"
                  @update:modelValue="reasonDialog"
                  data-testid="addcoupon-status"
                >
                  <!-- Slot for customizing each item -->
                  <template #item="{ item }" v-if="!isMobile()">
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <!-- Ensure `on` is passed directly for event handling -->
                        <div
                          style="cursor: pointer"
                          class="dropdown-item"
                          @click.stop="
                            () => {
                              coupon.status = item.value
                              $refs.statusSelect.blur()
                            }
                          "
                        >
                          <v-chip
                            v-bind="props"
                            style="cursor: pointer"
                            class="dropdown-item mr-2"
                            color="black"
                            text-color="white"
                            rounded
                            size="small"
                          >
                            ?
                          </v-chip>
                          {{ item.title }}
                        </div>
                      </template>
                      <span>{{ tooltips[item.value] }}</span>
                    </v-tooltip>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row
              class="mb-4"
              v-if="['Draft', 'Archived', 'Paused'].includes(coupon.status)"
            >
              <v-col cols="12" sm="4">
                <label for="reason">Reason:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-textarea
                  id="reason"
                  v-model="coupon.reason"
                  density="compact"
                  rows="3"
                  placeholder="Reason for the current status"
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row align="center" class="mb-4">
              <v-col cols="12" sm="4">
                <label for="checkbox" class="form-label">Recurring:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-checkbox
                  id="checkbox"
                  v-model="coupon.isRecurring"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row v-if="coupon.isRecurring" class="mb-4">
              <v-col cols="12" sm="4">
                <label for="checkbox" class="form-label"></label>
              </v-col>
              <v-col cols="12" sm="8">
                <v-col cols="12" sm="1"></v-col>
                <v-select
                  id="recurringType"
                  v-model="coupon.recurringType"
                  :items="recurringOption"
                  label="Recurring Type"
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>

            <v-divider :thickness="5" class="mt-5"></v-divider>

            <v-card-title class="pb-2 pl-0 font-weight-bold"
              >Media<span
                style="
                  color: gray;
                  font-family: 400 !important;
                  font-size: 16px;
                "
              ></span>
            </v-card-title>

            <v-row class="mb-4">
              <v-col cols="12" sm="3">
                <label for="logo">Logo<span>*</span>:</label>
              </v-col>
              <v-col cols="12" sm="9">
                <div
                  ref="dropzoneLogo"
                  class="dropzone"
                  v-tooltip.top="'Max file-size 2 MB'"
                  data-testid="addcoupon-logo-dropzone"
                ></div>
              </v-col>
            </v-row>
            <!--
            <v-row class="mb-4">
              <v-col cols="12" sm="1"></v-col>
              <v-col cols="12" sm="2">
                <label for="image">Image:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <div ref="dropzoneCouponImage" class="dropzone"></div>
              </v-col>
            </v-row> -->
            <!--
            <v-row class="mb-4">
              <v-col cols="12" sm="1"></v-col>
              <v-col cols="12" sm="2">
                <label for="thumbnail">Thumbnail:</label>
              </v-col>
              <v-col cols="12" sm="8">
                <div ref="dropzoneThumbnail" class="dropzone"></div>
              </v-col>
            </v-row> -->

            <v-divider :thickness="5" class="mt-5"></v-divider>

            <v-card-title class="pb-2 pl-0 font-weight-bold"
              >Title Typography</v-card-title
            >

            <v-row>
              <!-- Font Weight & Style Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.fontWeight"
                  :items="fontWeights"
                  density="compact"
                  label="Font Weight"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Align Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.textAlign"
                  :items="textAlignOptions"
                  density="compact"
                  label="Text Align"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Transform Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.textTransform"
                  :items="textTransformOptions"
                  density="compact"
                  label="Text Transform"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Decoration Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.textDecoration"
                  :items="textDecorationOptions"
                  density="compact"
                  label="Text Decoration"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Font Size Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.fontSize"
                  label="Font Size (px)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Line Height Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.lineHeight"
                  label="Line Height (em)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Font Color Field -->
              <v-col cols="10" sm="6">
                <v-color-picker
                  class="testing"
                  v-model="fontProperties.fontColor"
                  label="Font Color"
                  density="compact"
                ></v-color-picker>
              </v-col>
            </v-row>
            <v-divider :thickness="2" class="mt-5"></v-divider>

            <v-card-title class="pb-2 pl-0 font-weight-bold"
              >Description Typography</v-card-title
            >

            <v-row>
              <!-- Title Field -->
              <v-col cols="12"> </v-col>

              <!-- Font Weight & Style Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.descriptionfontWeight"
                  :items="fontWeights"
                  density="compact"
                  label="Font Weight"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Align Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.descriptiontextAlign"
                  :items="textAlignOptions"
                  density="compact"
                  label="Text Align"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Transform Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.descriptiontextTransform"
                  :items="textTransformOptions"
                  density="compact"
                  label="Text Transform"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Decoration Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.descriptiontextDecoration"
                  :items="textDecorationOptions"
                  density="compact"
                  label="Text Decoration"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Font Size Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.descriptionfontSize"
                  label="Font Size (px)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Line Height Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.descriptionlineHeight"
                  label="Line Height (em)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Font Color Field -->
              <v-col cols="12" sm="6">
                <v-color-picker
                  v-model="fontProperties.descriptionfontColor"
                  label="Font Color"
                  density="compact"
                ></v-color-picker>
              </v-col>
            </v-row>
            <v-divider :thickness="2" class="mt-5"></v-divider>

            <v-card-title class="pb-2 pl-0 font-weight-bold"
              >Disclaimer Typography</v-card-title
            >

            <v-row>
              <!-- Title Field -->
              <v-col cols="12"> </v-col>

              <!-- Font Weight & Style Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.disclaimerfontWeight"
                  :items="fontWeights"
                  density="compact"
                  label="Font Weight"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Align Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.disclaimertextAlign"
                  :items="textAlignOptions"
                  density="compact"
                  label="Text Align"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Transform Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.disclaimertextTransform"
                  :items="textTransformOptions"
                  density="compact"
                  label="Text Transform"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Text Decoration Field -->
              <v-col cols="12" sm="6">
                <v-select
                  v-model="fontProperties.disclaimertextDecoration"
                  :items="textDecorationOptions"
                  density="compact"
                  label="Text Decoration"
                  item-value="value"
                  item-title="text"
                  required
                />
              </v-col>

              <!-- Font Size Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.disclaimerfontSize"
                  label="Font Size (px)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Line Height Field -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="fontProperties.disclaimerlineHeight"
                  label="Line Height (em)"
                  density="compact"
                  type="number"
                  required
                ></v-text-field>
              </v-col>

              <!-- Font Color Field -->
              <v-col cols="12" sm="6">
                <v-color-picker
                  v-model="fontProperties.disclaimerfontColor"
                  label="Font Color"
                  density="compact"
                ></v-color-picker>
              </v-col>
            </v-row>
            <v-divider :thickness="2" class="mt-5"></v-divider>

            <v-row justify="end">
              <v-col
                style="display: flex; margin-top: 40px; justify-content: end"
                class="auto-width"
              >
                <v-btn
                  color="primary"
                  @click="addCoupon"
                  data-testid="addcoupon-submit"
                >
                  Add Coupon
                </v-btn>
                <v-btn
                  outlined
                  color="black"
                  class="cancel-btn"
                  @click="cancel"
                  data-testid="addcoupon-cancel"
                >
                  Cancel
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
      <v-card style="max-width: 400px; width: 100%" class="sticky-preview">
        <article
          role="article"
          class="coupon-card"
          aria-labelledby="coupon?.title"
        >
          <v-img
            :src="coupon.logo || '/logo-placeholder.png'"
            alt="Card image"
            style="
              aspect-ratio: 16/16;
              object-fit: contain;
              background: #f5f5f5;
            "
            class="mb-2"
            aria-label="Coupon image"
          />
          <div class="coupon-title d-flex mt-2">
            <span class="color-field" :style="{ fontSize: styleTitle.fontSize }"
              >Title:
            </span>
            <div
              class="title-text"
              style="width: 100%; margin-left: 2px"
              :style="styleTitle"
            >
              {{ coupon?.title }}
            </div>
          </div>
          <div class="coupon-description d-flex text--secondary mt-3">
            <span
              class="color-field"
              :style="{ fontSize: styleDescription.fontSize }"
              >Description:
            </span>
            <div
              class="title-text"
              style="width: 100%; margin-left: 2px"
              :style="styleDescription"
            >
              {{ coupon.description }}
            </div>
          </div>

          <div class="disclaimer d-flex my-3">
            <span
              class="color-field"
              :style="{ fontSize: styleDisclaimer.fontSize }"
              >Disclaimer:
            </span>
            <span
              class="title-text"
              :style="styleDisclaimer"
              style="width: 100%; margin-left: 2px"
            >
              {{ coupon.disclaimer }}</span
            >
          </div>
          <div class="exp-info mt-3">
            <span class="color-field">Expiration Date: </span>
            {{ calculateExpirationInfo(new Date(coupon.endTime)) }}
          </div>

          <!-- Link Section -->
          <div v-if="coupon.link && coupon.linkLabel" class="mt-3">
            <a
              :href="coupon.link"
              target="_blank"
              rel="noopener noreferrer"
              class="coupon-link"
              style="
                display: inline-block;
                padding: 8px 16px;
                background-color: #007bff;
                color: white;
                text-decoration: none;
                border-radius: 4px;
                font-weight: 500;
              "
            >
              {{ coupon.linkLabel }}
            </a>
          </div>
        </article>
      </v-card>
    </div>
    <v-snackbar v-model="snackbar" :timeout="3000" color="success" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, reactive, watch, computed, onMounted, nextTick } from 'vue'
import { useCounterStore } from '@/stores/counter'

import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'
import { useToast } from 'vue-toastification'
import { convertLocalTimeToServerTime, isMobile } from '@/utils'

export default {
  components: {
    Dropzone,
    flatPickr,
  },
  emits: ['coupon-added', 'cancel-add'],
  setup(props, { emit }) {
    const isRecurring = ref(false)
    const showCalendar = ref(false)
    const store = useCounterStore()
    const snackbar = ref(false)
    const snackbarMessage = ref('')
    const toast = useToast()
    const dropzoneLogo = ref(null)
    const dropzoneCouponImage = ref(null)
    const dropzoneThumbnail = ref(null)

    const config = {
      enableTime: true,
      dateFormat: 'Y-m-d h:i K',
      minuteIncrement: 1,
      time_24hr: false,

      onReady(selectedDates, dateStr, instance) {
        const todayButton = document.createElement('button')
        todayButton.textContent = 'Today'
        todayButton.type = 'button'
        todayButton.className = 'flatpickr-today-button'
        todayButton.addEventListener('click', () => {
          instance.setDate(new Date(), true)
        })
        instance.calendarContainer.appendChild(todayButton)
      },
    }

    const recurringOption = ['Daily', 'Weekly', 'Monthly']

    const rules = {
      // required: value => !!value || 'This field is required.',
      required: value => (!!value ? '' : 'This field is required.'),
    }

    const loading = ref(false)

    const coupon = ref({
      title: '',
      description: '',
      disclaimer: '',
      startTime: '',
      endTime: '',
      design: '',
      listItems: '3',
      logo: '',
      // couponImage: '',
      // thumbnail: '',
      recurringType: null,
      isRecurring: false,
      usageLimit: '',
      minPurchaseAmount: '',
      discountType: '',
      status: 'Schedule',
      reason: '',
      link: '',
      linkLabel: '',
    })

    const errors = reactive({
      title: '',
      description: '',
      disclaimer: '',
      startTime: '',
      endTime: '',
      usageLimit: '',
      minPurchaseAmount: '',
    })

    const today = ref(new Date().toISOString().split('T')[0])

    const formatDateTime = dateTime => {
      const date = new Date(dateTime)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = '00'
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }

    const fontProperties = ref({
      fontWeight: '',
      descriptionfontWeight: '',
      disclaimerfontWeight: '',
      textAlign: '',
      descriptiontextAlign: '',
      disclaimertextAlign: '',
      textTransform: '',
      descriptiontextTransform: '',
      disclaimertextTransform: '',
      textDecoration: '',
      descriptiontextDecoration: '',
      disclaimertextDecoration: '',
      fontSize: '',
      descriptionfontSize: '',
      disclaimerfontSize: '',
      lineHeight: '',
      descriptionlineHeight: '',
      disclaimerlineHeight: '',
      fontColor: '#000000',
      descriptionfontColor: '#000000',
      disclaimerfontColor: '#000000',
    })

    const styleDisclaimer = computed(() => ({
      color: fontProperties.value.disclaimerfontColor,
      fontSize: fontProperties.value.disclaimerfontSize
        ? `${fontProperties.value.disclaimerfontSize}px`
        : '',
      fontWeight: fontProperties.value.disclaimerfontWeight,
      textAlign: fontProperties.value.disclaimertextAlign,
      textTransform: fontProperties.value.disclaimertextTransform,
      textDecoration: fontProperties.value.disclaimertextDecoration,
      lineHeight: fontProperties.value.disclaimerlineHeight
        ? `${fontProperties.value.disclaimerlineHeight}em`
        : '',
    }))
    const styleTitle = computed(() => ({
      color: fontProperties.value.fontColor,
      fontSize: fontProperties.value.fontSize
        ? `${fontProperties.value.fontSize}px`
        : '',
      fontWeight: fontProperties.value.fontWeight,
      textAlign: fontProperties.value.textAlign,
      textTransform: fontProperties.value.textTransform,
      textDecoration: fontProperties.value.textDecoration,
      lineHeight: fontProperties.value.lineHeight
        ? `${fontProperties.value.lineHeight}em`
        : '',
    }))

    const styleDescription = computed(() => ({
      color: fontProperties.value.descriptionfontColor,
      fontSize: fontProperties.value.descriptionfontSize
        ? `${fontProperties.value.descriptionfontSize}px`
        : '',
      fontWeight: fontProperties.value.descriptionfontWeight,
      textAlign: fontProperties.value.descriptiontextAlign,
      textTransform: fontProperties.value.descriptiontextTransform,
      textDecoration: fontProperties.value.descriptiontextDecoration,
      lineHeight: fontProperties.value.descriptionlineHeight
        ? `${fontProperties.value.descriptionlineHeight}em`
        : '',
    }))

    const fontWeights = ref([
      { text: 'Normal 400', value: 400 },
      { text: 'Bold 700', value: 700 },
    ])

    const textAlignOptions = ref([
      { text: 'Left', value: 'left' },
      { text: 'Center', value: 'center' },
      { text: 'Right', value: 'right' },
      { text: 'Initial', value: 'initial' },
    ])

    const textTransformOptions = ref([
      { text: 'None', value: 'none' },
      { text: 'Capitalize', value: 'capitalize' },
      { text: 'Lowercase', value: 'lowercase' },
      { text: 'Uppercase', value: 'uppercase' },
    ])

    const textDecorationOptions = ref([
      { text: 'None', value: 'none' },
      { text: 'Underline', value: 'underline' },
      { text: 'Line Through', value: 'line-through' },
      { text: 'Overline', value: 'overline' },
    ])

    const dynamicFields = ref([])
    const valid = ref(false)
    const dialog = ref(false)
    const designOptions = ['Design 1', 'Design 2', 'Design 3']
    const discountOptions = [
      'none',
      'percentage',
      'fixed_amount',
      'free_shipping',
    ]

    const statusOptions = [
      { value: 'Draft', title: 'Draft' },
      { value: 'Archived', title: 'Archived' },
      { value: 'Paused', title: 'Paused' },
      { value: 'Schedule', title: 'Schedule' },
      { value: 'Active', title: 'Active' },
    ]

    const tooltips = {
      Draft:
        'Refers to a COUPON that is in Draft mode and not active.The coupon must have been Active before being set this Status',
      Archived:
        'Refers to a COUPON that is Archived and no longer usable.The coupon must have been Active before being set this Status',
      Paused:
        'Refers to a COUPON that is Paused the Coupon temporarily.The coupon must have been Active before being set this Status',
      Schedule:
        'Set a DATE/TIME to today to change the STATUS of a COUPON to ACTIVE (Ensure the time is not in the past Time must be exact match or in future)',
      Active:
        'STATUS of a COUPON that is live on the site. Must have an EXPIRATION date in the future.And Start Date Not in future',
    }

    const filteredStatusOptions = computed(() => {
      return statusOptions.filter(status => status !== coupon.value.status)
    })

    const onImageChange = (field, event) => {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = () => {
          const base64String = reader.result
          coupon.value[field] = base64String
        }
        reader.readAsDataURL(file)
      }
    }

    function updateFields() {
      dynamicFields.value = Array.from(
        { length: dynamicFields.value.length },
        () => Array(Number(coupon.value.listItems)).fill(''),
      )
    }

    function addField() {
      dynamicFields.value.push(Array(Number(coupon.value.listItems)).fill(''))
    }

    function removeField() {
      if (dynamicFields.value.length > 0) {
        dynamicFields.value.pop()
      }
    }

    const StatusChange = async () => {
      console.log(
        'StatusChange triggered - updating status based on start date',
      )
      const today = new Date()
      const todayDate = new Date(today)
      const formattedToday = todayDate.toISOString().slice(0, 16)
      const startDate = new Date(coupon.value.startTime)
      const startDateString = startDate.toISOString().slice(0, 16)

      const currentTime = coupon.value.startTime

      // Store the previous status for comparison
      const previousStatus = coupon.value.status

      // Determine the appropriate status based on start date
      if (startDate > todayDate) {
        // Start date is in the future - set to Schedule
        if (coupon.value.status !== 'Schedule') {
          coupon.value.status = 'Schedule'
          console.log(
            `Status automatically changed from ${previousStatus} to Schedule (future start date)`,
          )
        }
      } else {
        // Start date is in the past or current - set to Active
        if (coupon.value.status !== 'Active') {
          coupon.value.status = 'Active'
          console.log(
            `Status automatically changed from ${previousStatus} to Active (past/current start date)`,
          )
        }
      }
    }

    const initializeDropzone = (
      dropzoneElement,
      existingFileName,
      targetField,
    ) => {
      if (!Dropzone) return

      const dropzone = new Dropzone(dropzoneElement, {
        url: '/upload',
        autoProcessQueue: false,
        addRemoveLinks: true,
        maxFilesize: 1,
        acceptedFiles: 'image/*',
      })

      if (existingFileName) {
        const mockFile = { name: existingFileName }
        dropzone.emit('addedfile', mockFile)
        dropzone.files.push(mockFile)
        coupon.value[targetField] = existingFileName // Set initial value
      }

      dropzone.on('addedfile', file => {
        const reader = new FileReader()
        reader.onload = () => {
          file.base64 = reader.result
          coupon.value[targetField] = reader.result // Update reactive property
        }
        dropzone.on('removedfile', () => {
          coupon.value[targetField] = null // Clear the reactive property
        })
        reader.readAsDataURL(file)
      })

      dropzoneElement.dropzone = dropzone
    }

    const reasonDialog = () => {
      console.log('check status', coupon.value.status)
      dialog.value = true
    }

    const submitReason = async () => {
      dialog.value = false
      coupon.value.reason = coupon.value.reason
    }

    const getDropzoneFile = dropzoneRef => {
      if (dropzoneRef.value && dropzoneRef.value.dropzone) {
        const dropzone = dropzoneRef.value.dropzone
        if (dropzone.files.length > 0) {
          const file = dropzone.files[0]
          return file.base64 || ''
        }
      }
      return ''
    }
    const couponPayload = () => {
      const logoBase64 = getDropzoneFile(dropzoneLogo)

      // const couponImageBase64 = getDropzoneFile(dropzoneCouponImage)
      // const thumbnailBase64 = getDropzoneFile(dropzoneThumbnail)
      return {
        isRecurring: coupon.value.isRecurring,
        recurringType: coupon.value.recurringType,
        startTime: coupon.value.startTime || '',
        endTime: coupon.value.endTime || '',
        usageLimit: coupon.value.usageLimit || '',
        minPurchaseAmount: coupon.value.minPurchaseAmount || '',
        discountType: coupon.value.discountType || '',
        design: {
          title: coupon.value.title || '',
          description: coupon.value.description || '',
          disclaimer: coupon.value.disclaimer || '',
          media: {
            logo: logoBase64 || '',
            // couponImage: couponImageBase64 || '',
            // thumbnail: thumbnailBase64 || '',
          },
        },
        typography: {
          title: {
            fontWeight: fontProperties.value.fontWeight || 'normal',
            descriptionfontWeight:
              fontProperties.value.descriptionfontWeight || 'normal',
            disclaimerfontWeight:
              fontProperties.value.disclaimerfontWeight || 'normal',
            fontSize: parseInt(fontProperties.value.fontSize) || 16,
            descriptionfontSize:
              parseInt(fontProperties.value.descriptionfontSize) || 16,
            disclaimerfontSize:
              parseInt(fontProperties.value.disclaimerfontSize) || 16,
            lineHeight: parseInt(fontProperties.value.lineHeight) || 1.2,
            descriptionlineHeight:
              parseInt(fontProperties.value.descriptionlineHeight) || 1.2,
            disclaimerlineHeight:
              parseInt(fontProperties.value.disclaimerlineHeight) || 1.2,
            // height: parseInt(fontProperties.value.height) || 24,
            fontColor: fontProperties.value.fontColor || '#000000',
            descriptionfontColor:
              fontProperties.value.descriptionfontColor || '#000000',
            disclaimerfontColor:
              fontProperties.value.disclaimerfontColor || '#000000',
            textAlign: fontProperties.value.textAlign || 'left',
            descriptiontextAlign:
              fontProperties.value.descriptiontextAlign || 'left',
            disclaimertextAlign:
              fontProperties.value.disclaimertextAlign || 'left',
            textDecoration: fontProperties.value.textDecoration || 'none',
            descriptiontextDecoration:
              fontProperties.value.descriptiontextDecoration || 'none',
            disclaimertextDecoration:
              fontProperties.value.disclaimertextDecoration || 'none',
            textTransform: fontProperties.value.textTransform || 'none',
            descriptiontextTransform:
              fontProperties.value.descriptiontextTransform || 'none',
            disclaimertextTransform:
              fontProperties.value.disclaimertextTransform || 'none',
          },
        },
        callToAction: {
          text: coupon.value.linkLabel || '',
          link: coupon.value.link || '',
        },
        // walletAppearance: {
        //   shortTitle: coupon.value.shortTitle || 'Discount',
        //   subheader: coupon.value.subheader || 'Special Offer',
        //   header: coupon.value.header || 'Limited Time Offer!',
        //   backgroundColor: coupon.value.backgroundColor || '#FFFFFF',
        //   fontColor: coupon.value.fontColor || '#000000',
        //   barcodeType: coupon.value.barcodeType || 'QR',
        //   showCouponImageOn: coupon.value.showCouponImageOn || [
        //     'Google Wallet',
        //     'Apple Wallet',
        //   ],
        // },
        status: coupon.value.status || '',
        reason: coupon.value.reason || '',
      }
    }

    const validateForm = () => {
      errors.title = rules.required(coupon.value.title)
      errors.description = rules.required(coupon.value.description)
      errors.disclaimer = rules.required(coupon.value.disclaimer)
      errors.startTime = rules.required(coupon.value.startTime)
      errors.endTime = rules.required(coupon.value.endTime)
      errors.minPurchaseAmount = rules.required(coupon.value.minPurchaseAmount)
      errors.usageLimit = rules.required(coupon.value.usageLimit)

      const hasErrors = !Object.values(errors).every(error => error === '')

      const dz = dropzoneLogo.value?.dropzone
      if (!dz || dz.files.length === 0) {
        toast.error('Please upload a Logo image')
        return false
      }

      // Grab the first file
      const file = dz.files[0]
      const maxBytes = 2 * 1024 * 1024 // 2 MB
      if (file.size > maxBytes) {
        toast.error('Logo file size must be under 2 MB')
        return false
      }

      const logoBase64 = getDropzoneFile(dropzoneLogo)

      // Image validation
      if (!logoBase64) {
        toast.error('Please upload Logo Image')
        return false
      }
      if (hasErrors) {
        toast.error('Please fill all the required fields')
      }

      return !hasErrors // Return `true` if no errors, `false` otherwise
    }

    const addCoupon = async () => {
      if (validateForm()) {
        loading.value = true
        try {
          const payload = couponPayload()
          const newVal = coupon.value.status
          const oldVal = coupon.value.previousStatus || null

          const startDate = new Date(coupon.value.startTime)

          const today = new Date()

          if (newVal === 'Active') {
            if (startDate > today) {
              toast.error(
                `Cannot set Active status for a coupon starting in the future. Current Start Date is ${startDate.toLocaleString()}`,
              )
              return
            }
          } else if (newVal === 'Schedule') {
            const startDateObj = new Date(startDate)
            const todayObj = new Date(today)

            if (startDateObj <= todayObj) {
              toast.error(
                `Before Schedule ensure that the date is in the future. Current Start Date is ${startDateObj.toLocaleString()}`,
              )
              return
            }
          } else if (newVal === 'Draft') {
            coupon.value.status = newVal
          } else if (newVal === 'Paused' || newVal === 'Archived') {
            toast.error('This status only applies to a Active coupon.')
            return
          }
          console.log(payload)

          payload.startTime = convertLocalTimeToServerTime(
            coupon.value.startTime,
          )
          payload.endTime = convertLocalTimeToServerTime(coupon.value.endTime)

          const result = await store.createCoupon(payload)
          toast.success('Coupon added successfully!')
          // Emit the coupon-added event with the new coupon ID
          emit('coupon-added', result?.couponId || '')
        } catch (error) {
          console.error('Error adding coupon:', error)
        } finally {
          loading.value = false
        }
      }
    }
    4
    onMounted(() => {
      if (dropzoneCouponImage.value) {
        initializeDropzone(
          dropzoneCouponImage.value,
          coupon.value.couponImage,
          'couponImage',
        )
      }
      if (dropzoneLogo.value) {
        initializeDropzone(dropzoneLogo.value, coupon.value.logo, 'logo')
      }
      if (dropzoneThumbnail.value) {
        initializeDropzone(
          dropzoneThumbnail.value,
          coupon.value.thumbnail,
          'thumbnail',
        )
      }
    })
    const cancel = () => {
      console.log('am here in cancel')
      emit('cancel-add')
    }

    watch(
      () => coupon.value.recurringType,
      newStatus => {
        console.log('Status changed toww:', newStatus)
        showCalendar.value = false
      },
    )
    watch(
      () => coupon.value.isRecurring,
      newStatus => {
        if (newStatus) {
          console.log('status changes or nor')
          coupon.value.recurringType = null
          showCalendar.value = true
        }
        if (!newStatus) {
          coupon.value.recurringType = null
          showCalendar.value = false
        }
        console.log('Status changed to:', newStatus)
      },
    )

    watch(
      () => coupon.value.startTime,
      (newValue, oldValue) => {
        const normalizeDate = date => {
          if (!date) return null
          const parsedDate = new Date(date)
          return isNaN(parsedDate)
            ? date
            : parsedDate.toISOString().slice(0, 16)
        }

        const normalizedNewValue = normalizeDate(newValue)
        const normalizedOldValue = normalizeDate(oldValue)

        console.log('Normalized New Value:', normalizedNewValue)
        console.log('Normalized Old Value:', normalizedOldValue)

        if (normalizedNewValue !== normalizedOldValue) {
          StatusChange()
        }
      },
    )

    watch(
      () => coupon.value.endTime,
      (newValue, oldValue) => {
        const normalizeDate = date => {
          if (!date) return null
          const parsedDate = new Date(date)
          return isNaN(parsedDate)
            ? date
            : parsedDate.toISOString().slice(0, 16)
        }

        const normalizedNewValue = normalizeDate(newValue)
        const normalizedOldValue = normalizeDate(oldValue)

        console.log('Normalized New Value:', normalizedNewValue)
        console.log('Normalized Old Value:', normalizedOldValue)

        if (normalizedNewValue !== normalizedOldValue) {
          // EndStatusChange()
        }
      },
    )

    watch(
      () => coupon.value.status,
      (newVal, oldVal) => {
        if (newVal === 'Draft') {
          console.log('check value in wathc', newVal)
          reasonDialog()
        }
      },
    )

    const onSelectChange = newStatus => {
      console.log('Selected value:', newStatus)
      showCalendar.value = false
    }
    const calculateExpirationInfo = endDate => {
      const currentDateObj = new Date()
      const timeDiff = endDate - currentDateObj

      if (timeDiff > 0) {
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
        if (daysDiff >= 1) {
          return daysDiff === 1 ? 'Tomorrow' : `${daysDiff} days left`
        } else {
          const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60))
          const minutesDiff = Math.floor(
            (timeDiff % (1000 * 60 * 60)) / (1000 * 60),
          )
          return `${hoursDiff} hour(s) and ${minutesDiff} minute(s) Left`
        }
      } else {
        return ''
      }
    }

    const handleClickDatePicker = e => {
      e.target.showPicker()
    }

    return {
      recurringOption,
      isRecurring,
      showCalendar,
      coupon,
      today,
      loading,
      config,
      fontProperties,

      fontWeights,
      textAlignOptions,
      textTransformOptions,
      textDecorationOptions,
      rules,
      valid,
      reasonDialog: reasonDialog,
      dialog,
      designOptions,
      discountOptions,
      filteredStatusOptions,
      statusOptions,
      tooltips,
      addCoupon,
      onImageChange,
      StatusChange,
      updateFields,
      addField,
      removeField,
      cancel,
      snackbar,
      snackbarMessage,
      submitReason,
      dropzoneLogo,
      dropzoneCouponImage,
      dropzoneThumbnail,
      initializeDropzone,
      validateForm,
      errors,
      styleDescription,
      styleTitle,
      styleDisclaimer,
      calculateExpirationInfo,
      handleClickDatePicker,
      isMobile,
    }
  },
}
</script>

<style scoped>
#coupon-builder-admin :deep(.v-label.v-field-label) {
  margin-left: 12px !important;
}

#coupon-builder-admin :deep(.v-select .v-field .v-field__input > input) {
  background: none;
  border: none;
  box-shadow: none;
}

#coupon-builder-admin :deep(.v-field__input) {
  background: none;
  border: none;
}

#coupon-builder-admin :deep(.v-field--variant-outlined .v-field__input) {
  padding: 12px 16px;
}

#coupon-builder-admin :deep(.v-field--variant-filled .v-field__input) {
  box-shadow: none;
  line-height: 1.625em;
  padding: 24px 16px 4px 16px;
}

#coupon-builder-admin
  :deep(
    .v-field.v-field--variant-outlined .v-field__field input[type='text']:focus
  ) {
  box-shadow: none !important;
}

#coupon-builder-admin
  :deep(
    .v-field.v-field--variant-outlined
      .v-field__field
      input[type='number']:focus
  ) {
  box-shadow: none !important;
}

@media (max-width: 425px) {
  #coupon-builder-admin :deep(.v-expansion-panel-text__wrapper) {
    padding-left: 10px;
    padding-right: 10px;
  }

  #coupon-builder-admin :deep(.v-card-text) {
    padding-left: 5px;
    padding-right: 5px;
  }

  #coupon-builder-admin :deep(.v-field__input) {
    padding: 10px !important;
  }

  #coupon-builder-admin
    :deep(
      .v-expansion-panel-text.filters-section
        .v-expansion-panel-text__wrapper
        > div
    ) {
    margin: 0;
  }
}

.sticky-preview {
  position: sticky;
  top: 50px;
  max-height: calc(100vh - 30px);
  /* Viewport height minus offset */
  overflow-y: auto;

  .pad-to-add-coup {
    padding: 10px !important;
  }
}

.auto-width {
  width: auto;
}

.cancel-btn {
  margin-left: 5px;
}

.color-field {
  color: gray;
  font-weight: 600;
}

.v-progress-circular {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
}

.datepicker-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.custom-datepicker {
  width: 100%;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f9f9f9;
  color: #1b1a1a;
  font-size: larger;
  border-bottom: 1px solid;
  border-radius: none;
  border-radius: 0;
}

.custom-datepicker::placeholder {
  color: #aaa;
}

.calendar-icon {
  position: absolute;
  right: 10px;
  pointer-events: none;
}

input.custom-datepicker.flatpickr-input::placeholder {
  color: black;
}

.dropdown-label {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.styled-dropdown {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 8px;
  background-color: #fff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}

.dropdown-item {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.v-select-menu {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

label span {
  color: red;
}

.error-message {
  color: #a21d1d;
  margin-top: 0.25rem;
  margin-left: 10px;
  font-size: 13px;
}

.dropzone {
  overflow: hidden;
}

.coupon-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
  background-color: #ffffff;
  transition: transform 0.3s ease;
  margin-bottom: 20px;
  overflow: hidden;
}

.coupon-card:hover {
  transform: translateY(-5px);
}

.v-card {
  padding: 16px;
  min-height: 640px;
}

.text-center {
  text-align: center;
}

.grey--text {
  background: #f3f5f7;
  color: rgb(30, 0, 0);
  padding: 3px 5px;
  border-radius: 5px;
}

.custom-divider {
  height: 3px;
  background-color: #000;
}

.v-img {
  height: fit-content !important;
}

.custom-class {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.title-text {
  /* allow wrapping even without spaces */
  overflow-wrap: break-word;
  word-break: break-all;

  /* ensure it can break onto multiple lines */
  white-space: normal;
}

@media only screen and (max-width: 900px) {
  .custom-class {
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  .sticky-preview {
    position: static;
    top: 50px;
    max-height: calc(100vh - 30px);
    /* Viewport height minus offset */
    overflow-y: auto;
  }

  .pad-to-add-coup {
    padding: 0px !important;
  }

  .datepicker-wrapper {
    display: none;
  }
}

@media (min-width: 901px) {
  .mobile-only {
    display: none !important;
  }
}
</style>
